// relative
import { NAV_CONFIG } from './navigationConfig';
import type {
  NavigationContext,
  NavItemConfig,
  NavSectionConfig,
} from './navigationTypes';

/**
 * Calculate how well a path matches the current pathname
 * Returns the length of the match (higher = better match)
 */
export function getPathMatchScore(path: string, pathname: string): number {
  const normalize = (p: string) => {
    const withSlash = p.startsWith('/') ? p : `/${p}`;
    return withSlash.endsWith('/') && withSlash !== '/'
      ? withSlash.slice(0, -1)
      : withSlash;
  };

  const normalizedPath = normalize(path);
  const normalizedPathname = normalize(pathname);

  if (normalizedPathname === normalizedPath) return normalizedPath.length;
  if (normalizedPathname.startsWith(normalizedPath + '/'))
    return normalizedPath.length;
  return 0;
}

/**
 * Find the best matching navigation path for the current pathname
 */
export function findBestMatchingPath(pathname: string): string | null {
  let bestScore = 0;
  let bestPath: string | null = null;

  const checkItem = (item: NavItemConfig) => {
    if (item.path) {
      const score = getPathMatchScore(item.path, pathname);
      if (score > bestScore) {
        bestScore = score;
        bestPath = item.path;
      }
    }
    item.children?.forEach(checkItem);
  };

  NAV_CONFIG.forEach((section) => section.items.forEach(checkItem));
  return bestPath;
}

/**
 * Strip account ID from pathname for matching
 */
export function normalizePathnameForMatching(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length > 0 && /^\d+$/.test(segments[0])) {
    return '/' + segments.slice(1).join('/');
  }
  return pathname;
}

/**
 * Filter navigation items based on visibility conditions
 */
export function filterNavItems(
  items: NavItemConfig[],
  context: NavigationContext,
): NavItemConfig[] {
  return items
    .filter((item) => {
      // Check visibility condition
      if (item.visible && !item.visible(context)) {
        return false;
      }
      return true;
    })
    .map((item) => {
      // Recursively filter children
      if (item.children) {
        return {
          ...item,
          children: filterNavItems(item.children, context),
        };
      }
      return item;
    })
    .filter((item) => {
      // Remove items with no visible children
      if (item.children && item.children.length === 0) {
        return false;
      }
      return true;
    });
}

/**
 * Filter navigation sections based on visibility conditions
 */
export function filterNavSections(
  sections: NavSectionConfig[],
  context: NavigationContext,
): NavSectionConfig[] {
  return sections
    .filter((section) => {
      // Check section visibility condition
      if (section.visible && !section.visible(context)) {
        return false;
      }
      return true;
    })
    .map((section) => ({
      ...section,
      items: filterNavItems(section.items, context),
    }))
    .filter((section) => {
      // Remove sections with no visible items
      return section.items.length > 0;
    });
}
